import React, { useEffect, useState } from "react"
import { Platform } from "~/ui/primitives"
import { ColumnFlex, CenterFlex, Text, BetweenFlex, Button } from "~components/El"
import RNDateTimePicker, {
  DateTimePickerAndroid,
  type DateTimePickerEvent,
} from "~/ui/datetimepicker"
import type { PopupContextType } from "~/components/Popup/types"

export type DatePickerProps = {
  /** 当前选中的日期 */
  value?: Date
  /** 弹层标题 */
  title?: string
  minimumDate?: Date
  maximumDate?: Date
  /** 点击确定时返回选中的日期 */
  onChange?: (date: Date) => void
  onCancel?: () => void
  /** 关闭弹层（确定或取消后调用，可与 usePopup 配合） */
  handleClose?: () => void
}

export type OpenDatePickerInPopupParams = Pick<
  DatePickerProps,
  "title" | "value" | "minimumDate" | "maximumDate"
> & {
  onConfirm: (date: Date) => void
}

/** H5 / iOS：底部弹层 + 滚轮；Android 走 `DateTimePickerAndroid.open` */
const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onCancel,
  onChange,
  handleClose,
  minimumDate = new Date(1990, 0, 1),
  maximumDate = new Date(),
  title = "选择时间",
}) => {
  const [date, setDate] = useState(() => value ?? new Date())

  useEffect(() => {
    if (value) setDate(value)
  }, [value])

  const close = () => {
    handleClose?.()
  }

  const onConfirm = () => {
    onChange?.(date)
    close()
  }

  const onDismiss = () => {
    onCancel?.()
    close()
  }

  return (
    <ColumnFlex width="100%" padding="12px 16px 16px" gap="12px">
      <BetweenFlex width="100%" alignItems="center">
        <Button bg="transparent" px={4} py={8} onClick={onDismiss}>
          <Text color="secondaryText" fontSize="16px">
            取消
          </Text>
        </Button>
        <Text flex={1} textAlign="center" color="text" fontSize="16px" fontWeight="600">
          {title}
        </Text>
        <Button bg="transparent" px={4} py={8} onClick={onConfirm}>
          <Text color="buttonBackground" fontSize="16px" fontWeight="600">
            确定
          </Text>
        </Button>
      </BetweenFlex>
      <CenterFlex width="100%">
        <RNDateTimePicker
          style={{ width: "100%", height: 200 }}
          value={date}
          mode="date"
          display="spinner"
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          onChange={(_, selectedDate) => {
            if (selectedDate) setDate(selectedDate)
          }}
        />
      </CenterFlex>
    </ColumnFlex>
  )
}

/**
 * Android：系统日期对话框（`DateTimePickerAndroid.open`），不使用 `popup.open`。
 * iOS：底部弹层 + 内嵌 `DatePicker`。
 */
export function openDatePickerInBottomPopup(
  popup: Pick<PopupContextType, "open" | "close">,
  params: OpenDatePickerInPopupParams
) {
  const { onConfirm, title, value, minimumDate, maximumDate } = params

  if (Platform.OS === "android") {
    DateTimePickerAndroid.open({
      value: value ?? new Date(),
      mode: "date",
      minimumDate,
      maximumDate,
      title,
      onChange: (event: DateTimePickerEvent, date?: Date) => {
        if (event.type !== "set" || !date) return
        onConfirm(date)
      },
    })
    return
  }

  const p = popup.open({
    type: "bottom",
    content: (
      <DatePicker
        title={title}
        value={value}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        handleClose={() => popup.close(p.id)}
        onChange={onConfirm}
      />
    ),
  })
}

export default DatePicker
